import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSession } from '../session';
import { Sheet } from '../platform/Sheet';
import { AttributeFields } from '../platform/AttributeFields';
import type { TodoLogEntry } from '@familyhub/shared';
import { tdb, type TodoRecord } from './db';
import {
  addTodo,
  updateTodo,
  toggleDone,
  moveTodo,
  moveTodoTo,
  deleteTodo,
  renameCategory,
  deleteCategory,
  isTodoOpen,
  todoState,
} from './actions';
import { memberColor } from './people';
import {
  isoToDateInput,
  isoToTimeInput,
  inputsToIso,
  formatWhen,
  formatDue,
  formatDay,
  dayOffset,
  isPast,
  describeRecurrence,
  describeLogEntry,
} from './datetime';
import { Scheduler, RecurrenceEditor, AssigneePicker, useRecurrence } from './editors';
import {
  CheckIcon,
  ClockIcon,
  RepeatIcon,
  CalendarIcon,
  PersonIcon,
  GripIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from './icons';

/** The label the general (uncategorised) group shows — null category is "Algemeen". */
const GENERAL_LABEL = 'Algemeen';

/** Today's `YYYY-MM-DD` in local time — the default date a repeat needs. */
function todayInput(): string {
  return isoToDateInput(new Date().toISOString());
}

/** The three lenses on the list: everything, what is due now, and what is mine. */
type Lens = 'all' | 'today' | 'mine';

/** Whether a todo is on the hook for *today* — open, scheduled, and due now or overdue. */
function isDueNow(t: TodoRecord): boolean {
  return isTodoOpen(t) && t.scheduled_at != null && (isPast(t.scheduled_at) || dayOffset(t.scheduled_at) <= 0);
}

/**
 * The Todo module — "Taken". One shared, ordered checklist for the household.
 *
 * A quick-add bar at the top sets a task and, in one step, its date, who it is
 * for and whether it repeats. Below it the list is grouped by category; a lens
 * row narrows it to what is due today, or to what is assigned to you. Within a
 * group the open todos sit in the household's own order (drag by the handle, or
 * the ↑/↓ keys); everything ticked off — finished one-offs and recurring todos
 * resting until their next turn — collapses into a "Klaar" disclosure.
 *
 * Everything reads from the local mirror, so it works with no signal; a tap on a
 * todo opens a sheet to edit who, when, and the wording, or to remove it.
 */
export function TodosScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : '';
  const me = session.status === 'in' ? session.me : null;

  const members = useMemo(() => {
    if (me?.members?.length) return me.members;
    return userId ? [{ id: userId, display_name: 'Jij' }] : [];
  }, [me, userId]);
  const nameOf = (id: string | null): string | null =>
    id ? members.find((m) => m.id === id)?.display_name ?? '?' : null;
  const memberIds = useMemo(() => members.map((m) => m.id), [members]);
  const colorOf = (id: string | null): string | null => memberColor(id, memberIds);

  const rows = useLiveQuery(
    () => tdb.todos.filter((t) => !t.deleted_at).toArray(),
    [],
    [] as TodoRecord[],
  );
  const pending = useLiveQuery(() => tdb.outbox.count(), [], 0);

  const [lens, setLens] = useState<Lens>('all');
  const dueCount = useMemo(() => rows.filter(isDueNow).length, [rows]);

  // Group every visible todo by category, and within each split the three states
  // (open / waiting-for-next-turn / done). The general (null) group heads as
  // "Algemeen"; named categories follow, alphabetical. A category with nothing to
  // show under the current lens drops out entirely.
  const groups = useMemo(() => {
    const inLens = (t: TodoRecord): boolean => {
      if (lens === 'today') return isDueNow(t);
      if (lens === 'mine') return t.assigned_to === userId;
      return true;
    };
    const byCat = new Map<string, TodoRecord[]>();
    for (const t of rows) {
      if (!inLens(t)) continue;
      byCat.set(t.category ?? '', [...(byCat.get(t.category ?? '') ?? []), t]);
    }
    const named = [...byCat.keys()].filter((c) => c !== '').sort((a, b) => a.localeCompare(b, 'nl'));
    const order = byCat.has('') ? ['', ...named] : named;
    const byPos = (a: TodoRecord, b: TodoRecord) => a.position - b.position;
    return order
      .map((cat) => {
        const all = byCat.get(cat)!;
        return {
          category: cat,
          open: all.filter((t) => todoState(t) === 'open').sort(byPos),
          waiting: all.filter((t) => todoState(t) === 'waiting').sort(byPos),
          done: all.filter((t) => todoState(t) === 'done').sort(byPos),
        };
      })
      .filter((g) => g.open.length || g.waiting.length || g.done.length);
  }, [rows, lens, userId]);

  // Every category in use, alphabetical — offered when picking and managed in the sheet.
  const categories = useMemo(
    () => [...new Set(rows.map((t) => t.category).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b, 'nl')),
    [rows],
  );

  const [editing, setEditing] = useState<TodoRecord | null>(null);
  const [managing, setManaging] = useState(false);
  const [prefillCategory, setPrefillCategory] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // The group heading's "+": prefill the quick-add category and focus the field.
  const addInto = (category: string) => {
    setPrefillCategory(category);
    inputRef.current?.focus();
  };

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">‹ Hub</Link>
        <h1>Taken</h1>
        <button
          className="btn btn-quiet"
          aria-label="Categorieën beheren"
          onClick={() => setManaging(true)}
          disabled={categories.length === 0}
        >
          Categorieën
        </button>
      </div>

      {session.status === 'in' && pending > 0 && (
        <div className="banner" role="status" aria-live="polite">
          {pending} {pending === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op synchronisatie
        </div>
      )}

      <AddBar
        ref={inputRef}
        members={members}
        nameOf={nameOf}
        categories={categories}
        userId={userId || null}
        prefillCategory={prefillCategory}
        onPrefillConsumed={() => setPrefillCategory('')}
      />

      {rows.length > 0 && (
        <div className="chips todo-lenses">
          <button className="chip" aria-pressed={lens === 'all'} onClick={() => setLens('all')}>
            Alles
          </button>
          <button className="chip" aria-pressed={lens === 'today'} onClick={() => setLens('today')}>
            Vandaag
            {dueCount > 0 && <span className="todo-lens-count">{dueCount}</span>}
          </button>
          {members.length > 1 && (
            <button className="chip" aria-pressed={lens === 'mine'} onClick={() => setLens('mine')}>
              Van mij
            </button>
          )}
        </div>
      )}

      <main>
        {rows.length === 0 ? (
          <div className="todo-empty">
            <p className="todo-empty-lead">Nog niets te doen.</p>
            <p className="todo-empty-hint">
              Met <b>Datum</b>, <b>Wie</b> en <b>Herhalen</b> zet je er meteen een herinnering bij.
            </p>
          </div>
        ) : groups.length === 0 ? (
          <p className="todo-empty-lens">
            {lens === 'today' ? 'Niets voor vandaag.' : 'Niets aan jou toegewezen.'}
          </p>
        ) : (
          groups.map(({ category, open, waiting, done }) => (
            <section className="group" key={category || '__none'}>
              <h2 className="group-head">
                <span>{category || GENERAL_LABEL}</span>
                <button
                  className="todo-add-here"
                  aria-label={`Taak toevoegen in ${category || GENERAL_LABEL}`}
                  onClick={() => addInto(category)}
                >
                  +
                </button>
              </h2>

              {open.length > 0 && (
                <TodoGroup
                  todos={open}
                  reorderable={lens === 'all'}
                  nameOf={nameOf}
                  colorOf={colorOf}
                  userId={userId || null}
                  onOpen={setEditing}
                />
              )}

              {(waiting.length > 0 || done.length > 0) && (
                <details className="todo-done-block">
                  <summary className="group-subhead">
                    Klaar <span className="todo-when-text">{waiting.length + done.length}</span>
                  </summary>
                  <ul className="card todo-list todo-done-list">
                    {/* A recurring todo done for this cycle rests here too — dimmed
                        but not struck through, showing "Volgende: «datum»". Ticking
                        it makes it disappear from the active list into this section;
                        it returns on the day of its next deadline (§3). */}
                    {waiting.map((t) => (
                      <TodoRow
                        key={t.id}
                        todo={t}
                        state="waiting"
                        who={nameOf(t.assigned_to)}
                        color={colorOf(t.assigned_to)}
                        onToggle={() => void toggleDone(t.id, userId || null)}
                        onOpen={() => setEditing(t)}
                      />
                    ))}
                    {done.map((t) => (
                      <TodoRow
                        key={t.id}
                        todo={t}
                        state="done"
                        who={nameOf(t.assigned_to)}
                        color={colorOf(t.assigned_to)}
                        onToggle={() => void toggleDone(t.id, userId || null)}
                        onOpen={() => setEditing(t)}
                      />
                    ))}
                  </ul>
                </details>
              )}
            </section>
          ))
        )}
      </main>

      {editing && (
        <TodoSheet
          todo={editing}
          members={members}
          categories={categories}
          userId={userId || null}
          onClose={() => setEditing(null)}
        />
      )}

      {managing && (
        <CategoriesSheet categories={categories} userId={userId || null} onClose={() => setManaging(false)} />
      )}
    </div>
  );
}

/**
 * The quick-add bar. The task name owns the first line; a row of attribute chips
 * below it opens an inline panel to set the date, who it is for, and a repeat —
 * so a scheduled, assigned, recurring chore is one screen, not a second trip into
 * the edit sheet. Category is picked (or created) on the last line. The name
 * field is forwarded so a group heading's "+" can focus it.
 */
const AddBar = forwardRef<
  HTMLInputElement,
  {
    members: { id: string; display_name: string }[];
    nameOf: (id: string | null) => string | null;
    categories: string[];
    userId: string | null;
    prefillCategory: string;
    onPrefillConsumed: () => void;
  }
>(function AddBar({ members, nameOf, categories, userId, prefillCategory, onPrefillConsumed }, ref) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [submitCount, setSubmitCount] = useState(0);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [assignee, setAssignee] = useState('');
  const rec = useRecurrence(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState('');

  // The group heading's "+" prefills the category; consume it once.
  useEffect(() => {
    if (prefillCategory) {
      setCategory(prefillCategory);
      onPrefillConsumed();
    }
  }, [prefillCategory, onPrefillConsumed]);

  const options = category && !categories.includes(category) ? [...categories, category] : categories;
  const scheduledIso = date ? inputsToIso(date, time) : null;
  const focusName = () => {
    if (ref && typeof ref !== 'function') ref.current?.focus();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || !rec.repeatValid) return;
    // A repeat needs an anchor instant; default it to today 09:00 if none was set.
    const effectiveDate = rec.recurrence && !date ? todayInput() : date;
    const scheduled_at = effectiveDate ? inputsToIso(effectiveDate, time) : null;
    const recurrence = rec.recurrence;
    const keepCategory = category;
    setTitle('');
    setDate('');
    setTime('');
    setAssignee('');
    rec.reset();
    setSubmitCount((c) => c + 1);
    await addTodo(
      { title: trimmed, assigned_to: assignee || null, scheduled_at, recurrence, category: keepCategory || null },
      userId,
    );
    setCategory(keepCategory);
    focusName();
  };

  const confirmNewCat = () => {
    const name = newCat.trim();
    if (name) setCategory(name);
    setNewCat('');
    setAddingCat(false);
  };

  return (
    <form className="todo-add" onSubmit={submit}>
      <input
        ref={ref}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Nieuwe taak"
        aria-label="Nieuwe taak"
        enterKeyHint="done"
      />

      <AttributeFields
        resetKey={submitCount}
        fields={[
          {
            key: 'date',
            icon: <CalendarIcon />,
            label: 'Datum',
            set: !!date,
            value: scheduledIso ? formatDue(scheduledIso) : undefined,
            panel: <Scheduler date={date} time={time} onChange={(d, t) => { setDate(d); setTime(t); }} />,
          },
          {
            key: 'who',
            icon: <PersonIcon />,
            label: 'Wie',
            hidden: members.length <= 1,
            set: !!assignee,
            value: assignee ? nameOf(assignee) : undefined,
            panel: <AssigneePicker members={members} value={assignee} onChange={setAssignee} />,
          },
          {
            key: 'repeat',
            icon: <RepeatIcon />,
            label: 'Herhalen',
            set: !!rec.recurrence,
            value: rec.recurrence ? describeRecurrence(rec.recurrence) : undefined,
            panel: <RecurrenceEditor rec={rec} />,
          },
        ]}
      />

      <div className="todo-add-foot">
        {addingCat ? (
          <>
            <input
              type="text"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmNewCat(); } }}
              placeholder="Nieuwe categorie"
              aria-label="Nieuwe categorie"
              autoFocus
            />
            <button type="button" className="btn btn-quiet" onClick={confirmNewCat}>Klaar</button>
          </>
        ) : (
          <>
            <select
              className="todo-add-cat"
              value={category}
              onChange={(e) => { if (e.target.value === '__new') setAddingCat(true); else setCategory(e.target.value); }}
              aria-label="Categorie voor nieuwe taak"
            >
              <option value="">{GENERAL_LABEL}</option>
              {options.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="__new">+ Nieuwe categorie…</option>
            </select>
            <button className="btn btn-primary" type="submit" disabled={!title.trim() || !rec.repeatValid}>
              Toevoegen
            </button>
          </>
        )}
      </div>
    </form>
  );
});

/** Enough of a hold not to fire on a scroll flick, but the grip is the handle. */
const SLOP_PX = 4;

/**
 * One category's open todos, as a reorderable list.
 *
 * Reorder is a drag from the grip handle on the right of each row — a visible
 * affordance, so touch users see that rows move without discovering a hidden
 * long-press. The drop is a single-row float write (`moveTodoTo`); the page
 * scroll is locked only while a drag is live. Keyboard users get the same reorder
 * through the ↑/↓ buttons, which reveal on focus. When the list is filtered
 * (a lens other than "Alles") reordering is off — the order it would write is
 * against a partial list.
 */
function TodoGroup({
  todos,
  reorderable,
  nameOf,
  colorOf,
  userId,
  onOpen,
}: {
  todos: TodoRecord[];
  reorderable: boolean;
  nameOf: (id: string | null) => string | null;
  colorOf: (id: string | null) => string | null;
  userId: string | null;
  onOpen: (t: TodoRecord) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const mids = useRef<number[]>([]);
  const down = useRef<{ x: number; y: number } | null>(null);
  const [drag, setDrag] = useState<{ id: string; origin: number; startY: number; y: number; slot: number } | null>(null);

  // Lock the page scroll only while a drag is live, so the browser cannot pan the
  // list out from under the finger. Cleaned up when the drag ends or unmounts.
  useEffect(() => {
    if (!drag) return;
    document.body.classList.add('todo-reordering');
    return () => document.body.classList.remove('todo-reordering');
  }, [drag !== null]);

  const rowMids = (): number[] => {
    const ul = listRef.current;
    if (!ul) return [];
    return [...ul.querySelectorAll('.todo-row')].map((el) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2;
    });
  };

  const slotFor = (clientY: number, origin: number): number => {
    let k = 0;
    mids.current.forEach((mid, i) => {
      if (i !== origin && mid < clientY) k++;
    });
    return k;
  };

  const onGripDown = (e: React.PointerEvent, index: number, id: string) => {
    if (todos.length < 2 || e.button > 0) return;
    down.current = { x: e.clientX, y: e.clientY };
    const el = e.currentTarget as HTMLElement;
    try { el.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
    mids.current = rowMids();
    setDrag({ id, origin: index, startY: e.clientY, y: e.clientY, slot: index });
  };

  const onGripMove = (e: React.PointerEvent) => {
    if (!drag) return;
    e.preventDefault();
    const y = e.clientY;
    setDrag((d) => (d ? { ...d, y, slot: slotFor(y, d.origin) } : d));
  };

  const onGripUp = () => {
    if (!drag) return;
    const { id, slot, origin, startY, y } = drag;
    setDrag(null);
    down.current = null;
    // A grip tap with no real movement is not a reorder.
    if (slot === origin && Math.abs(y - startY) < SLOP_PX) return;
    void moveTodoTo(todos, id, slot);
  };

  const rest = drag ? todos.filter((t) => t.id !== drag.id) : [];
  const dropBeforeId = drag ? rest[drag.slot]?.id ?? null : null;
  const dropEnd = drag ? drag.slot >= rest.length : false;
  const dragDy = drag ? drag.y - drag.startY : 0;

  return (
    <ul ref={listRef} className={`card todo-list${dropEnd ? ' drop-end' : ''}`}>
      {todos.map((t, i) => (
        <TodoRow
          key={t.id}
          todo={t}
          state="open"
          who={nameOf(t.assigned_to)}
          color={colorOf(t.assigned_to)}
          onToggle={() => void toggleDone(t.id, userId)}
          onOpen={() => onOpen(t)}
          reorderable={reorderable && todos.length > 1}
          onUp={i > 0 ? () => void moveTodo(todos, t.id, -1) : undefined}
          onDown={i < todos.length - 1 ? () => void moveTodo(todos, t.id, 1) : undefined}
          dragging={drag?.id === t.id}
          dragStyle={drag?.id === t.id ? { transform: `translateY(${dragDy}px)` } : undefined}
          dropBefore={dropBeforeId === t.id}
          onGripPointerDown={(e) => onGripDown(e, i, t.id)}
          onGripPointerMove={onGripMove}
          onGripPointerUp={onGripUp}
        />
      ))}
    </ul>
  );
}

function TodoRow({
  todo,
  state,
  who,
  color,
  onToggle,
  onOpen,
  reorderable = false,
  onUp,
  onDown,
  dragging = false,
  dragStyle,
  dropBefore = false,
  onGripPointerDown,
  onGripPointerMove,
  onGripPointerUp,
}: {
  todo: TodoRecord;
  state: 'open' | 'waiting' | 'done';
  who: string | null;
  color: string | null;
  onToggle: () => void;
  onOpen: () => void;
  reorderable?: boolean;
  onUp?: () => void;
  onDown?: () => void;
  dragging?: boolean;
  dragStyle?: React.CSSProperties;
  dropBefore?: boolean;
  onGripPointerDown?: (e: React.PointerEvent) => void;
  onGripPointerMove?: (e: React.PointerEvent) => void;
  onGripPointerUp?: (e: React.PointerEvent) => void;
}) {
  const checked = state !== 'open';
  const overdue = state === 'open' && todo.scheduled_at != null && isPast(todo.scheduled_at);

  // The meta line, built as spans (not a joined string) so an icon, a person's
  // colour dot and the date's tint each land on the right part.
  const segments: React.ReactNode[] = [];
  if (who) {
    segments.push(
      <span className="todo-seg todo-who" key="who">
        {color && <span className="todo-dot" style={{ background: color }} aria-hidden="true" />}
        {who}
      </span>,
    );
  }
  if (state === 'open' && todo.scheduled_at) {
    const tone = overdue ? 'overdue' : dayOffset(todo.scheduled_at) <= 0 ? 'today' : 'upcoming';
    segments.push(
      <span className={`todo-seg todo-due todo-due-${tone}`} key="due">
        {overdue && <ClockIcon />}
        <span className="todo-when-text">{formatDue(todo.scheduled_at)}</span>
        {overdue && <span className="todo-late">te laat</span>}
      </span>,
    );
  }
  if (state === 'waiting' && todo.scheduled_at) {
    segments.push(
      <span className="todo-seg todo-next" key="next">
        Volgende: <span className="todo-when-text">{formatDay(todo.scheduled_at)}</span>
      </span>,
    );
  }
  if (todo.recurrence) {
    segments.push(
      <span className="todo-seg todo-repeat" key="repeat">
        <RepeatIcon />
        {describeRecurrence(todo.recurrence)}
      </span>,
    );
  }

  return (
    <li
      className={`row todo-row todo-${state}${reorderable ? ' todo-reorderable' : ''}${dragging ? ' dragging' : ''}${dropBefore ? ' drop-before' : ''}`}
      style={dragStyle}
    >
      <button
        className={`todo-check${checked ? ' checked' : ''}`}
        aria-label={checked ? `Afvinken ongedaan maken: ${todo.title}` : `Afvinken: ${todo.title}`}
        aria-pressed={checked}
        onClick={onToggle}
      >
        {checked && <CheckIcon />}
      </button>

      <button className="row-main todo-main" onClick={onOpen} aria-label={`Taak bewerken: ${todo.title}`}>
        <span className="row-name">{todo.title}</span>
        {segments.length > 0 && (
          <span className="row-sub todo-sub">
            {segments.map((seg, i) => (
              <span className="todo-seg-wrap" key={i}>
                {i > 0 && <span className="todo-sep" aria-hidden="true">·</span>}
                {seg}
              </span>
            ))}
          </span>
        )}
      </button>

      {reorderable && (
        <>
          <span
            className="todo-grip"
            aria-hidden="true"
            onPointerDown={onGripPointerDown}
            onPointerMove={onGripPointerMove}
            onPointerUp={onGripPointerUp}
            onPointerCancel={onGripPointerUp}
          >
            <GripIcon />
          </span>
          <span className="todo-reorder">
            <button className="todo-move" aria-label="Omhoog" onClick={onUp} disabled={!onUp}>
              <ChevronUpIcon />
            </button>
            <button className="todo-move" aria-label="Omlaag" onClick={onDown} disabled={!onDown}>
              <ChevronDownIcon />
            </button>
          </span>
        </>
      )}
    </li>
  );
}

function TodoSheet({
  todo,
  members,
  categories,
  userId,
  onClose,
}: {
  todo: TodoRecord;
  members: { id: string; display_name: string }[];
  categories: string[];
  userId: string | null;
  onClose: () => void;
}) {
  const nameOf = (id: string | null): string | null =>
    id ? members.find((m) => m.id === id)?.display_name ?? null : null;
  const [title, setTitle] = useState(todo.title);
  const [assignee, setAssignee] = useState(todo.assigned_to ?? '');
  const [category, setCategory] = useState(todo.category ?? '');
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState('');
  // Every known category, plus the current one if it is a not-yet-saved new label.
  const chips = category && !categories.includes(category) ? [...categories, category] : categories;
  const confirmNewCat = () => {
    const name = newCat.trim();
    if (name) setCategory(name);
    setNewCat('');
    setAddingCat(false);
  };
  const [date, setDate] = useState(todo.scheduled_at ? isoToDateInput(todo.scheduled_at) : '');
  const [time, setTime] = useState(todo.scheduled_at ? isoToTimeInput(todo.scheduled_at) : '');
  const rec = useRecurrence(todo.recurrence);

  const save = async () => {
    const trimmed = title.trim();
    if (!trimmed || !rec.repeatValid) return;
    // A recurring todo is anchored to a due time — default it to today 09:00 if
    // none was set, so the reminder sweep and the roll-forward have an instant.
    const effectiveDate = rec.recurrence && !date ? todayInput() : date;
    const scheduled_at = effectiveDate ? inputsToIso(effectiveDate, time) : null;
    await updateTodo(
      todo.id,
      { title: trimmed, assigned_to: assignee || null, category: category.trim() || null, scheduled_at, recurrence: rec.recurrence },
      userId,
    );
    onClose();
  };

  const remove = async () => {
    await deleteTodo(todo.id);
    onClose();
  };

  return (
    <Sheet title="Taak" onClose={onClose}>
      <label className="sheet-field">
        <span className="sheet-label">Wat</span>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Lamp vervangen" />
      </label>

      {members.length > 1 && <AssigneePicker members={members} value={assignee} onChange={setAssignee} />}

      <div className="sheet-field">
        <span className="sheet-label">Categorie</span>
        <div className="sheet-row todo-cats">
          {chips.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              aria-pressed={category === c}
              onClick={() => setCategory(category === c ? '' : c)}
            >
              {c}
            </button>
          ))}
          {addingCat ? (
            <span className="sheet-row todo-newcat">
              <input
                type="text"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmNewCat(); } }}
                placeholder="Nieuwe categorie"
                aria-label="Nieuwe categorie"
                autoFocus
              />
              <button type="button" className="btn btn-quiet" onClick={confirmNewCat}>Klaar</button>
            </span>
          ) : (
            <button
              type="button"
              className="chip todo-newcat-open"
              aria-label="Nieuwe categorie toevoegen"
              onClick={() => setAddingCat(true)}
            >
              +
            </button>
          )}
        </div>
      </div>

      <RecurrenceEditor rec={rec} />

      <div className="sheet-field">
        <span className="sheet-label">{rec.repeat === 'none' ? 'Herinnering' : 'Vanaf'}</span>
        <Scheduler date={date} time={time} onChange={(d, t) => { setDate(d); setTime(t); }} />
        {rec.recurrence && !date && <span className="todo-hint">Zonder datum: vandaag om 09:00</span>}
      </div>

      <div className="sheet-row todo-sheet-actions">
        <button className="btn btn-primary" onClick={() => void save()} disabled={!title.trim() || !rec.repeatValid}>Opslaan</button>
        <button className="btn btn-danger" onClick={() => void remove()}>Verwijderen</button>
      </div>

      <TodoLog log={todo.log} nameOf={nameOf} />
    </Sheet>
  );
}

/**
 * A todo's little activity log — what happened, by whom, and when — newest
 * first. Absent until the todo has a history.
 */
function TodoLog({
  log,
  nameOf,
}: {
  log: TodoLogEntry[] | null;
  nameOf: (id: string | null) => string | null;
}) {
  if (!log || log.length === 0) return null;
  const entries = [...log].reverse();
  return (
    <div className="sheet-field todo-log">
      <span className="sheet-label">Geschiedenis</span>
      <ul className="todo-log-list">
        {entries.map((e, i) => (
          <li key={i} className="todo-log-item">
            <span className="todo-log-what">{describeLogEntry(e)}</span>
            <span className="todo-log-meta">
              {[nameOf(e.by), formatWhen(e.at)].filter(Boolean).join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Manage the set of categories: rename one everywhere it is used, or remove it
 * (the todos stay, they just lose the label). A category is only ever a string
 * on todos, so both are bulk edits through the normal outbox (see actions.ts).
 */
function CategoriesSheet({
  categories,
  userId,
  onClose,
}: {
  categories: string[];
  userId: string | null;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const startRename = (c: string) => { setEditing(c); setDraft(c); };
  const commitRename = async () => {
    if (editing) await renameCategory(editing, draft, userId);
    setEditing(null);
    setDraft('');
  };

  return (
    <Sheet title="Categorieën" onClose={onClose}>
      {categories.length === 0 && <p className="agenda-empty">Nog geen categorieën.</p>}
      {categories.map((c) => (
        <div className="sheet-row todo-cat-manage" key={c}>
          {editing === c ? (
            <>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void commitRename(); } }}
                aria-label={`Naam voor ${c}`}
                autoFocus
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-primary" onClick={() => void commitRename()} disabled={!draft.trim()}>Opslaan</button>
              <button type="button" className="btn btn-quiet" onClick={() => setEditing(null)}>Annuleren</button>
            </>
          ) : (
            <>
              <span className="row-name" style={{ flex: 1 }}>{c}</span>
              <button type="button" className="btn btn-quiet" aria-label={`${c} hernoemen`} onClick={() => startRename(c)}>Hernoemen</button>
              <button type="button" className="btn btn-danger" aria-label={`${c} verwijderen`} onClick={() => void deleteCategory(c, userId)}>Verwijderen</button>
            </>
          )}
        </div>
      ))}
    </Sheet>
  );
}
